import { useMemo, useState } from 'react';

import { CustomTransaction, Transaction } from '../../../Overview/types';
import {
  ComplexParamsBundle,
  TransactionDetailsApiResponse,
  LatestBotState,
  Bot,
} from '../../types';
import TransactionItem from './Item';
import CustomTransactionItem from './Item/Custom';
import DoubleTransactionItem from './Item/DoubleItem';

import './index.css';
import LoadingIcons from 'react-loading-icons';
import {
  CbrtRangeState,
  CompactTransaction,
  CurrentProfit,
  MrcState,
  ProfitResultIndicatorConfigs,
  WakeupTimes,
} from '../../../ProfitCalculations/types';
import ComplexParamsBundleComponent from './Item/ComplexParamsBundle';
import LegacyIndicator from './Item/LegacyIndicator';
import { compareComplexParamsBundle } from '../../../../helpers/bundleTransactionsToCompactForm';
import LatestBotStateComponent from './Item/LatestBotState';

interface Props {
  transactions: TransactionDetailsApiResponse | undefined;
  profitCalcsTransactions?: Transaction[] | undefined;
  isLoading: boolean;
  compactTransactionLength?: number | undefined;
  currentComplexParams?: ComplexParamsBundle;
  latestBotState?: LatestBotState;
  isDoubleView?: boolean;
  transactionsByTimestamp?: Record<string, Transaction[]>;
  profitCalcsCurrentProfit: CurrentProfit | undefined;
  cbrtRangeStates?: CbrtRangeState[];
  mrcStates?: MrcState[] | undefined;
  wakeupTimes?: WakeupTimes;
  bot?: Bot;
  complexParamsChangeTimestamps?: number[];
  indicatorConfig?: ProfitResultIndicatorConfigs;
}

const isCustomTransaction = (
  transaction: Transaction | CustomTransaction,
): transaction is CustomTransaction =>
  transaction.transactionType.indexOf('_BALANCE') !== -1;

const isTransaction = (
  item: Transaction | ComplexParamsBundle,
): item is Transaction => item.hasOwnProperty('transactionType');

const isBotLatestState = (
  item: Transaction | ComplexParamsBundle | LatestBotState,
): item is LatestBotState => item.hasOwnProperty('cbrtRangeStates');

const TransactionList: React.FC<Props> = (props: Props) => {
  const {
    transactions,
    isLoading,
    compactTransactionLength,
    currentComplexParams,
    isDoubleView,
    transactionsByTimestamp,
    profitCalcsTransactions,
    profitCalcsCurrentProfit,
    cbrtRangeStates,
    mrcStates,
    latestBotState,
    wakeupTimes,
    bot,
    complexParamsChangeTimestamps,
    indicatorConfig,
  } = props;

  const items = useMemo(() => {
    const compactTransactions = transactions?.compactTransactions;
    const takeProfitTransactions =
      transactions?.allTransactions.filter((t) =>
        t.transactionType.includes('TAKE_PROFIT'),
      ) || [];

    if (!compactTransactions?.length) {
      return [];
    }

    const items: Array<
      | string
      | ComplexParamsBundle
      | LatestBotState
      | Transaction
      | Transaction[]
    > = [];

    if (currentComplexParams) {
      items.push(currentComplexParams);
    }

    /** Push latest state */
    if (latestBotState) {
      items.push(latestBotState);
    }

    let complexParamsBundle = currentComplexParams;
    let isLegacyPushed = false;

    let data;

    if (isDoubleView && transactionsByTimestamp) {
      /** Use transactionsByTimestamp */
      const entries = Object.entries(transactionsByTimestamp).sort(
        (a, b) => parseFloat(b[0]) - parseFloat(a[0]),
      );
      data = entries.map((e) => e[1]);
    } else {
      data = [...compactTransactions, ...takeProfitTransactions].sort(
        (a, b) => b.transactionTimestamp - a.transactionTimestamp,
      );
    }

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];
      let transaction: Transaction;

      if (Array.isArray(datum)) {
        transaction = datum[0];
      } else {
        transaction = datum;
      }

      if (
        !complexParamsBundle &&
        !!transaction.complexParams &&
        !!transaction.timeFrames
      ) {
        complexParamsBundle = {
          complexParams: transaction.complexParams,
          timeFrames: transaction.timeFrames,
        };

        items.push(complexParamsBundle);
        items.push(datum);
        continue;
      }

      if (!complexParamsBundle) {
        if (!isLegacyPushed) {
          items.push('LEGACY');
          isLegacyPushed = true;
        }

        items.push(datum);
        continue;
      }

      /** If compact transaction has different complex params bundle
       * add that param bundle first
       */

      if (!transaction.complexParams || !transaction.timeFrames) {
        if (!isLegacyPushed) {
          items.push('LEGACY');
          isLegacyPushed = true;
        }

        items.push(datum);
        continue;
      }

      const hasSameComplexParamsBundle = true;
      // const hasSameComplexParamsBundle = compareComplexParamsBundle(
      //   complexParamsBundle,
      //   transaction,
      // );

      if (hasSameComplexParamsBundle) {
        items.push(datum);
        continue;
      }

      complexParamsBundle = {
        complexParams: transaction.complexParams,
        timeFrames: transaction.timeFrames,
      };

      items.push(complexParamsBundle);
      items.push(datum);
    }

    return items;
  }, [
    currentComplexParams,
    isDoubleView,
    latestBotState,
    transactions?.allTransactions,
    transactions?.compactTransactions,
    transactionsByTimestamp,
  ]);

  const compactTransactionWithoutCustomTransactions = useMemo(() => {
    return transactions?.compactTransactions
      .filter((t) => !isCustomTransaction(t))
      .sort((a, b) => a.transactionTimestamp - b.transactionTimestamp);
  }, [transactions?.compactTransactions]);

  const renderTransactionItem = (
    item:
      | Transaction
      | Transaction[]
      | ComplexParamsBundle
      | LatestBotState
      | string,
    index: number,
  ) => {
    if (!transactions) {
      return null;
    }

    if (typeof item === 'string') {
      return <LegacyIndicator />;
    }

    if (Array.isArray(item) && item.length === 2 && profitCalcsTransactions) {
      const realTransactionIndex =
        compactTransactionWithoutCustomTransactions?.findIndex(
          (t) => t.compactTransactionId === item[0].compactTransactionId,
        ) ?? 0;

      const realClusteredTransactions = transactions?.allTransactions.filter(
        (t) => t.compactTransactionId === item[0].compactTransactionId,
      );
      const profitClusteredTransactions = profitCalcsTransactions
        .filter(
          (t: any) => t.compactTransactionId === item[1].compactTransactionId,
        )
        .sort((a, b) => b.transactionTimestamp - a.transactionTimestamp);

      const clusteredTransactions = realClusteredTransactions.map((t, i) => [
        t,
        profitClusteredTransactions[i],
      ]);

      return (
        <DoubleTransactionItem
          key={index}
          index={index}
          transactions={item}
          compactTransactionIndex={realTransactionIndex}
          currentProfit={transactions.currentProfit?.currentProfit}
          currentPriceChange={transactions.currentProfit?.currentPriceChange}
          profitCalcsCurrentProfit={profitCalcsCurrentProfit}
          compactTransactionsLength={compactTransactionLength || 0}
          clusteredTransactions={clusteredTransactions}
        />
      );
    }

    const transaction = Array.isArray(item) ? item[0] : item;

    if (isBotLatestState(transaction)) {
      return (
        <LatestBotStateComponent
          latestBotState={transaction}
          complexParamsChangeTimestamps={complexParamsChangeTimestamps}
        />
      );
    }

    if (!isTransaction(transaction)) {
      return (
        <ComplexParamsBundleComponent
          complexParamsBundle={transaction}
          isFirst={index === 0}
        />
      );
    }

    if (isCustomTransaction(transaction)) {
      return (
        <CustomTransactionItem
          key={index}
          transaction={transaction}
          index={index}
          totalLength={transactions?.compactTransactions?.length}
        />
      );
    }

    if (transaction.transactionType.includes('TAKE_PROFIT')) {
      // compactTransactions are sorting from new to old
      const { previousCompactTransaction, nextCompactTransaction } =
        transactions.compactTransactions.reduce<{
          previousCompactTransaction: Transaction | null;
          nextCompactTransaction: Transaction | null;
        }>(
          (acc, curr) => {
            if (acc.nextCompactTransaction && acc.previousCompactTransaction) {
              return acc;
            }

            if (curr.transactionTimestamp > transaction.transactionTimestamp) {
              acc.nextCompactTransaction = curr;
            }

            if (curr.transactionTimestamp < transaction.transactionTimestamp) {
              acc.previousCompactTransaction = curr;
            }

            return acc;
          },
          { previousCompactTransaction: null, nextCompactTransaction: null },
        );

      // prev and next
      const compactTransactions: Transaction[] = [];

      if (previousCompactTransaction) {
        compactTransactions.push(previousCompactTransaction);
      }

      if (nextCompactTransaction) {
        compactTransactions.push(nextCompactTransaction);
      }

      const startTimestamp =
        transaction.candles?.multi?.[0]?.startTimestamp ?? 0;
      const endTimestamp =
        transaction.candles?.multi?.[transaction.candles?.multi?.length - 1]
          ?.endTimestamp ?? 0;

      const cpt = transactions.compactTransactions.filter(
        (t) =>
          startTimestamp <= t.transactionTimestamp &&
          t.transactionTimestamp <= endTimestamp,
      );

      cpt.push(transaction);

      return (
        <TransactionItem
          key={index}
          index={index}
          bot={bot}
          transaction={transaction}
          compactTransactionIndex={null}
          currentProfit={transactions.currentProfit?.currentProfit}
          currentPriceChange={transactions.currentProfit?.currentPriceChange}
          compactTransactionsLength={0}
          clusteredTransactions={[transaction]}
          compactTransactions={cpt as unknown as CompactTransaction[]}
          showCandles={!isDoubleView}
          cbrtRangeStates={cbrtRangeStates?.filter((s) =>
            transaction?.candles && transaction?.candles?.higher?.length > 0
              ? transaction.candles.higher[0].startTimestamp <= s.timestamp &&
                s.timestamp <=
                  transaction.candles.higher[
                    transaction.candles.higher.length - 1
                  ].endTimestamp
              : 0,
          )}
          mrcStates={mrcStates?.filter((s) =>
            transaction?.candles && transaction?.candles?.higher?.length > 0
              ? transaction.candles.higher[0].startTimestamp <=
                  s.startTimestamp &&
                s.startTimestamp <=
                  transaction.candles.higher[
                    transaction.candles.higher.length - 1
                  ].endTimestamp
              : 0,
          )}
          timeFrames={currentComplexParams?.timeFrames}
          showCurrentProfit={!latestBotState}
          wakeupTimes={wakeupTimes}
          indicatorConfig={indicatorConfig}
        />
      );
    }

    const compactTransactionId = transaction.compactTransactionId;
    const rootIndex =
      compactTransactionWithoutCustomTransactions
        ?.sort((a, b) => b.transactionTimestamp - a.transactionTimestamp)
        ?.findIndex(
          (t) =>
            !!t.compactTransactionId &&
            t.compactTransactionId.toString() === compactTransactionId,
        ) ?? 0;

    const compactTransactionList = compactTransactionWithoutCustomTransactions
      ? [...compactTransactionWithoutCustomTransactions].sort(
          (a, b) => b.transactionTimestamp - a.transactionTimestamp,
        )
      : [];
    const compactTransactions = [];

    if (rootIndex !== 0) {
      compactTransactions.push(compactTransactionList[rootIndex - 1]);
    }

    compactTransactions.push(compactTransactionList[Math.max(0, rootIndex)]);

    compactTransactions.push(
      compactTransactionList[
        Math.min(
          rootIndex + 1,
          compactTransactionWithoutCustomTransactions?.length
            ? compactTransactionWithoutCustomTransactions.length - 1
            : 0,
        )
      ],
    );

    const clusteredTransactions = transactions.allTransactions
      .filter(
        (t) => t.compactTransactionId === transaction.compactTransactionId,
      )
      .sort((a, b) => b.transactionTimestamp - a.transactionTimestamp);

    return (
      <TransactionItem
        key={index}
        index={index}
        bot={bot}
        transaction={transaction}
        compactTransactionIndex={
          compactTransactionWithoutCustomTransactions?.findIndex(
            (t) => t.compactTransactionId === transaction.compactTransactionId,
          ) ?? 0
        }
        currentProfit={transactions.currentProfit?.currentProfit}
        currentPriceChange={transactions.currentProfit?.currentPriceChange}
        compactTransactionsLength={compactTransactionLength || 0}
        clusteredTransactions={clusteredTransactions}
        compactTransactions={
          compactTransactions as unknown as CompactTransaction[]
        }
        showCandles={!isDoubleView}
        cbrtRangeStates={cbrtRangeStates?.filter((s) =>
          transaction?.candles && transaction?.candles?.higher?.length > 0
            ? transaction.candles.higher[0].startTimestamp <= s.timestamp &&
              s.timestamp <=
                transaction.candles.higher[
                  transaction.candles.higher.length - 1
                ].endTimestamp
            : 0,
        )}
        mrcStates={mrcStates?.filter((s) =>
          transaction?.candles && transaction?.candles?.higher?.length > 0
            ? transaction.candles.higher[0].startTimestamp <=
                s.startTimestamp &&
              s.startTimestamp <=
                transaction.candles.higher[
                  transaction.candles.higher.length - 1
                ].endTimestamp
            : 0,
        )}
        timeFrames={currentComplexParams?.timeFrames}
        showCurrentProfit={!latestBotState}
        wakeupTimes={wakeupTimes}
        indicatorConfig={indicatorConfig}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="d-loading-container">
        <LoadingIcons.Grid width={100} />
      </div>
    );
  }

  if (!transactions) {
    return (
      <span className="detail-transactions-no-transactions-found">
        No transactions found
      </span>
    );
  }

  return (
    <div className="detail-transactions-container">
      {items.map(renderTransactionItem)}
    </div>
  );
};

export default TransactionList;
